import React, { useState } from 'react';

const ACCENT = '#10B981';
const PURPLE = '#8B5CF6';
const CYAN = '#06B6D4';
const RED = '#EF4444';
const TEXT = 'var(--text)';
const MUTED = 'var(--muted)';
const BORDER = 'var(--border)';
const BG2 = 'var(--bg2)';

const MENTIONS = [
  { platform: '📱 Instagram', name: 'Instagram', text: 'Great service from this company!', sentiment: 'Positive' as const, time: '2h ago', action: 'Respond' as const },
  { platform: '🌐 Google', name: 'Google', text: '4 stars — Good experience overall', sentiment: 'Positive' as const, time: '4h ago', action: 'Respond' as const },
  { platform: '🐦 Twitter', name: 'Twitter', text: 'Waiting for response from support...', sentiment: 'Neutral' as const, time: '6h ago', action: 'Respond' as const },
  { platform: '📰 News Site', name: 'News', text: 'Local business expands operations', sentiment: 'Positive' as const, time: '1d ago', action: 'Report' as const },
  { platform: '😐 Facebook', name: 'Facebook', text: 'Had some issues with delivery', sentiment: 'Negative' as const, time: '1d ago', action: 'Respond' as const, danger: true },
];

const PLATFORMS = [
  { emoji: '⭐', name: 'Google Reviews', status: 'Active' as const },
  { emoji: '📘', name: 'Facebook', status: 'Active' as const },
  { emoji: '📸', name: 'Instagram', status: 'Active' as const },
  { emoji: '🐦', name: 'Twitter/X', status: 'Active' as const },
  { emoji: '▶️', name: 'YouTube Comments', status: 'Active' as const },
  { emoji: '📍', name: 'Google My Business', status: 'Active' as const },
  { emoji: '📰', name: 'News Sites', status: 'Active' as const },
  { emoji: '📞', name: 'JustDial', status: 'Active' as const },
  { emoji: '❓', name: 'Quora', status: 'Coming Soon' as const },
  { emoji: '🔴', name: 'Reddit', status: 'Coming Soon' as const },
  { emoji: '💼', name: 'LinkedIn', status: 'Coming Soon' as const },
  { emoji: '✅', name: 'Trustpilot', status: 'Coming Soon' as const },
];

const CRISIS_STEPS = [
  { t: '0 min', title: '🔍 Crisis Detected', desc: 'Negative keywords trigger alert' },
  { t: '5 min', title: '📊 Damage Assessment', desc: 'AI analyzes severity 1-10' },
  { t: '15 min', title: '📝 Response Drafted', desc: 'AI writes counter-response' },
  { t: '20 min', title: '👤 Human Review', desc: 'You approve or auto-publish' },
  { t: '30 min', title: '🚀 Counter Campaign', desc: 'Positive content pushed everywhere' },
];

function sentimentStyle(s: 'Positive' | 'Neutral' | 'Negative') {
  if (s === 'Positive') return { bg: `${ACCENT}22`, color: ACCENT };
  if (s === 'Negative') return { bg: `${RED}22`, color: '#F87171' };
  return { bg: BG2, color: MUTED };
}

export function BrandMonitorPage() {
  const [alerts, setAlerts] = useState({
    neg: true,
    sentiment: true,
    crisis: true,
    greview: false,
    news: false,
    competitor: true,
  });
  const [delivery, setDelivery] = useState({ wa: true, email: true, sms: false, push: false });

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between"
        style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 24 }}
      >
        <div className="min-w-0 flex-1 space-y-3">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            style={{ background: `${RED}22`, color: '#FCA5A5', border: `1px solid ${RED}55` }}
          >
            BRAND SAFETY
          </span>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: TEXT }}>
            Brand Safety Monitor
          </h1>
          <p className="max-w-xl text-sm leading-relaxed" style={{ color: MUTED }}>
            24/7 reputation monitoring across entire internet. Negative content detected & countered instantly.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {['24/7 Monitoring', '50+ Platforms', 'Real-time Alerts'].map((t) => (
            <span
              key={t}
              className="shrink-0 rounded-full border px-3 py-2 text-[11px] font-semibold"
              style={{ borderColor: `${RED}44`, background: `${RED}12`, color: '#FCA5A5' }}
            >
              {t}
            </span>
          ))}
        </div>
      </header>

      <section className="odc-card !p-8 text-center" style={{ transform: 'none' }}>
        <p className="mb-6 text-xs" style={{ color: MUTED }}>
          Connect accounts to get real scores
        </p>
        <div className="mx-auto flex justify-center">
          <div className="relative" style={{ width: 160, height: 160 }}>
            <div
              className="absolute rounded-full"
              style={{
                inset: 0,
                border: `6px solid rgba(16,185,129,0.2)`,
                pointerEvents: 'none',
              }}
            />
            <div
              className="absolute rounded-full"
              style={{
                inset: 6,
                background: `conic-gradient(${ACCENT} 0% 85%, rgba(255,255,255,0.08) 85% 100%)`,
              }}
            />
            <div
              className="absolute flex flex-col items-center justify-center rounded-full"
              style={{
                inset: 22,
                background: 'var(--card)',
                color: TEXT,
              }}
            >
              <span className="text-[40px] font-bold leading-none" style={{ color: ACCENT, fontFamily: "'Space Grotesk',sans-serif" }}>
                85
              </span>
              <span className="text-sm" style={{ color: MUTED }}>
                /100
              </span>
              <span className="mt-1 text-xs font-semibold" style={{ color: MUTED }}>
                Brand Health
              </span>
            </div>
          </div>
        </div>
        <span
          className="odc-badge mx-auto mt-4 inline-flex rounded-lg !px-3 !py-1 !text-xs font-bold"
          style={{ background: `${ACCENT}22`, color: ACCENT, border: `1px solid ${ACCENT}55` }}
        >
          Good
        </span>

        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-4 md:grid-cols-4">
          {(
            [
              { label: 'Online Reputation', value: '88/100', pct: 88, color: ACCENT, kind: 'bar' as const },
              { label: 'Review Score', value: '4.2/5', pct: 84, color: CYAN, kind: 'bar' as const },
              { label: 'Social Sentiment', value: '76% Positive', pct: 76, color: PURPLE, kind: 'bar' as const },
              { label: 'Crisis Risk', value: 'Low', pct: 20, color: ACCENT, kind: 'badge' as const },
            ] as const
          ).map((row) => (
            <div key={row.label} className="text-left">
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>
                {row.label}
              </p>
              <p className="mt-1 text-sm font-bold" style={{ color: TEXT }}>
                {row.value}
              </p>
              {row.kind === 'bar' ? (
                <div className="odc-progress mt-2 h-1.5 overflow-hidden rounded-full" style={{ background: BG2 }}>
                  <div className="h-full rounded-full" style={{ width: `${row.pct}%`, background: row.color }} />
                </div>
              ) : (
                <span
                  className="mt-2 inline-block rounded-md px-2 py-0.5 text-[10px] font-bold"
                  style={{ background: `${ACCENT}22`, color: ACCENT }}
                >
                  Low
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="odc-card !p-6" style={{ transform: 'none' }}>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold" style={{ color: TEXT }}>
              Recent Mentions
            </h2>
            <span className="odc-live-dot" />
          </div>
          <p className="mt-1 text-sm" style={{ color: MUTED }}>
            Last 24 hours across all platforms
          </p>
          <ul className="mt-4 space-y-3">
            {MENTIONS.map((m, i) => {
              const st = sentimentStyle(m.sentiment);
              return (
                <li
                  key={i}
                  className="rounded-xl border p-3"
                  style={{
                    borderColor: m.danger ? RED : BORDER,
                    background: BG2,
                    boxShadow: m.danger ? `0 0 0 1px ${RED}33` : undefined,
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-bold" style={{ color: TEXT }}>
                      {m.platform}
                    </span>
                    <span className="rounded px-2 py-0.5 text-[9px] font-bold uppercase" style={{ background: st.bg, color: st.color }}>
                      {m.sentiment}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-xs" style={{ color: MUTED }}>
                    {m.text}
                  </p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-[10px]" style={{ color: MUTED }}>
                      {m.time}
                    </span>
                    <button type="button" className="odc-btn-ghost !px-2 !py-1 !text-[10px]">
                      {m.action}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 text-center text-xs" style={{ color: MUTED }}>
            Connect social accounts to see real mentions
          </p>
        </div>

        <div className="odc-card !p-6" style={{ transform: 'none' }}>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold" style={{ color: TEXT }}>
              Alert Center
            </h2>
            <span className="text-lg">🔔</span>
          </div>
          <p className="mt-1 text-sm" style={{ color: MUTED }}>
            Automatic alerts when action needed
          </p>
          <ul className="mt-4 space-y-3">
            {(
              [
                { key: 'neg' as const, icon: '🚨', label: 'Negative review detected', on: alerts.neg },
                { key: 'sentiment' as const, icon: '📉', label: 'Sentiment drops below 60%', on: alerts.sentiment },
                { key: 'crisis' as const, icon: '🔥', label: 'Crisis keywords detected', on: alerts.crisis },
                { key: 'greview' as const, icon: '⭐', label: 'New Google review', on: alerts.greview },
                { key: 'news' as const, icon: '📱', label: 'Brand mentioned in news', on: alerts.news },
                { key: 'competitor' as const, icon: '💬', label: 'Competitor mentioned your brand', on: alerts.competitor },
              ] as const
            ).map((row) => (
              <li key={row.key} className="flex items-center justify-between gap-2">
                <span className="text-sm" style={{ color: MUTED }}>
                  {row.icon} {row.label}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={row.on}
                  className={`odc-toggle shrink-0 ${row.on ? 'active' : ''}`}
                  onClick={() => setAlerts((a) => ({ ...a, [row.key]: !a[row.key] }))}
                />
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs font-bold uppercase tracking-wider" style={{ color: MUTED }}>
            Send alerts via:
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {(
              [
                { key: 'wa' as const, label: 'WhatsApp' },
                { key: 'email' as const, label: 'Email' },
                { key: 'sms' as const, label: 'SMS' },
                { key: 'push' as const, label: 'Push' },
              ] as const
            ).map((p) => {
              const on = delivery[p.key];
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setDelivery((d) => ({ ...d, [p.key]: !d[p.key] }))}
                  className="rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: on ? ACCENT : BORDER,
                    background: on ? `${ACCENT}22` : BG2,
                    color: on ? ACCENT : MUTED,
                  }}
                >
                  {p.label}
                  {on ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
          <button type="button" className="odc-btn-ghost mt-4 w-full justify-center !py-2 !text-xs">
            Send Test Alert
          </button>
        </div>
      </div>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          Monitoring Coverage
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          Platforms we track for your brand
        </p>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="rounded-xl border p-3 text-center" style={{ borderColor: BORDER, background: BG2 }}>
              <div className="text-2xl">{p.emoji}</div>
              <p className="mt-1 text-xs font-bold" style={{ color: TEXT }}>
                {p.name}
              </p>
              <span
                className="mt-2 inline-block rounded-md px-2 py-0.5 text-[9px] font-bold uppercase"
                style={{
                  background: p.status === 'Active' ? `${ACCENT}22` : 'rgba(245,158,11,0.2)',
                  color: p.status === 'Active' ? ACCENT : '#FBBF24',
                }}
              >
                {p.status}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="odc-card !p-6" style={{ transform: 'none' }}>
        <h2 className="text-lg font-bold" style={{ color: TEXT }}>
          AI Crisis Response Playbook
        </h2>
        <p className="mt-1 text-sm" style={{ color: MUTED }}>
          When crisis detected — ODC acts in 30 minutes
        </p>
        <ol className="relative mx-auto mt-8 max-w-xl space-y-6 border-l-2 pl-8" style={{ borderColor: `${ACCENT}44` }}>
          {CRISIS_STEPS.map((step, i) => (
            <li key={step.t} className="relative">
              <span
                className="absolute -left-[41px] flex h-8 w-8 items-center justify-center rounded-full text-xs font-black text-white"
                style={{ background: ACCENT, top: 0 }}
              >
                {i + 1}
              </span>
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ACCENT }}>
                {step.t}
              </p>
              <p className="text-sm font-bold" style={{ color: TEXT }}>
                {step.title}
              </p>
              <p className="mt-1 text-xs" style={{ color: MUTED }}>
                {step.desc}
              </p>
            </li>
          ))}
        </ol>
      </section>

      <section
        className="odc-card !p-8 text-center"
        style={{
          transform: 'none',
          border: `2px solid transparent`,
          background: `linear-gradient(var(--card), var(--card)) padding-box, linear-gradient(135deg, ${RED}66, ${RED}22) border-box`,
        }}
      >
        <h2 className="text-xl font-bold" style={{ color: TEXT }}>
          Is your brand protected?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: MUTED }}>
          Enable Brand Safety Monitor to start 24/7 protection
        </p>
        <button type="button" className="odc-btn-primary mx-auto mt-5 justify-center !px-8 !py-3">
          Enable Monitoring
        </button>
      </section>
    </div>
  );
}
